import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactGA from 'react-ga4'
import { BasicInfoForm } from "./registration-pages/BasicInfoForm";
import { InsuranceForm } from "./registration-pages/InsuranceForm";
import { DiagnosticForm } from "./registration-pages/DiagnosticForm";
import { FinalResults } from "./registration-pages/FinalResults";
import { PersonalQuestionsForm } from "./registration-pages/PersonalQuestionsForm";
import { GetStartedForm } from "./registration-pages/GetStartedForm";
import { RegistrationProvider, useRegistration, RegistrationFormData } from "./context/registration-context";
import { convertAvailabilityScheduleToBackend } from "./utils/availability-schedule-util";

import type { UserDataResponse } from "./types/user-data.types";
import { AutismDiagnosisStatus, DiagnosticInfoDto, GetSignedUrlUploadType } from "./types/diagnostic.types";
import {  RegistrationStateData, RegistrationStep } from "./types/registration-state.types";
import { Header } from "../components/Header";
import { api } from "../../../services/api";
import { ShowToast } from "../../../services/toast";
import { fileUpload } from "../../../services/file-upload";
import { 
  getToken, 
  getUser, 
  getPartialToken, 
  setUser, 
  clearPartialToken, 
  setPartialToken, 
  setToken,
  clearCookies
} from "../../../services/utils";
import {
  determineRegistrationStep
} from "./utils/registration-util";

function FamilyRegistrationContent({ defaultStep }: { defaultStep?: number }) {
  const { userId } = useParams();
  const { formData, updateFormData, populateFormWithUserData, clearFormData } = useRegistration();
  
  const [registrationData, setRegistrationData] = useState<RegistrationStateData>({
    state: 'new',
    currentStep: RegistrationStep.GET_STARTED,
    loading: true
  });

  const [formError, setFormError] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [diagnosticSuccessShown, setDiagnosticSuccessShown] = useState(false);
  const navigate = useNavigate();

  // Validate complete user and handle redirects
  const validateCompleteUser = (userData: UserDataResponse) => {
    console.log('🔍 Validating complete user data:', userData);
    
    // Only validate patient/family users
    if (userData.type !== 'PF') {
      console.error('❌ Invalid user type - expected patient/family user');
      return false;
    }

    const patientData = userData as any; // Type assertion for validation
    
          // Use the utility function to determine the correct step
      const registrationData: Partial<RegistrationFormData> = {
        guardianFirstName: patientData.guardianFirstName,
        guardianLastName: patientData.guardianLastName,
        email: patientData.email,
        mobile: patientData.mobile,
        firstName: patientData.firstName,
        lastName: patientData.lastName,
        dob: patientData.dob,
        zipCode: patientData.zipCode,
        howDidYouHearAboutUs: patientData.howDidYouHearAboutUs,
        howDidYouHearAboutUsText: patientData.howDidYouHearAboutUsText,
        isInSchoolDaycare: patientData.isInSchoolDaycare,
        hasIEP: patientData.hasIEP,
        hasHadABAServices: patientData.hasHadABAServices,
        primarySupportAreas: patientData.primarySupportAreas,
        availabilitySchedule: patientData.availabilitySchedule,
        openToTelehealth: patientData.openToTelehealth,
        insuranceFiles: patientData.insuranceFiles,
        hasAutismDiagnosis: patientData.autismDiagnosis === 'YES' ? true : 
                            patientData.autismDiagnosis === 'NO' ? false : undefined,
        cdeUrl: patientData.cdeUrl,
        lmnUrl: patientData.lmnUrl,
        diagnosticProviderInfo: patientData.diagnosticProviderInfo,
        letterProviderInfo: patientData.letterProviderInfo,
      };
    
    const correctStep = determineRegistrationStep(registrationData, false);
    
    console.log('📋 User validation check - determined step:', correctStep);
    
    // If user has no guardian info, they're invalid
    if (correctStep === RegistrationStep.GET_STARTED) {
      console.error('❌ Invalid complete user - missing required fields');
      ShowToast({
        message: "Sorry, something went wrong.",
        type: "error"
      });
      
      // Clear the invalid user data and redirect to new registration
      clearPartialToken();
      setUser({});
      setRegistrationData(prev => ({
        ...prev,
        state: 'new',
        currentStep: RegistrationStep.GET_STARTED
      }));
      return false;
    }

    console.log('✅ Complete user validation passed - has required fields for step:', correctStep);
    
    // Populate form data with user data
    const formDataToUpdate: Partial<RegistrationFormData> = {
      guardianFirstName: patientData.guardianFirstName,
      guardianLastName: patientData.guardianLastName,
      email: patientData.email || '',
      mobile: patientData.mobile || '',
      firstName: patientData.firstName,
      lastName: patientData.lastName,
      dob: patientData.dob,
      zipCode: patientData.zipCode,
      howDidYouHearAboutUs: patientData.howDidYouHearAboutUs,
      howDidYouHearAboutUsText: patientData.howDidYouHearAboutUsText,
      privacyConsent: false // Default value since not in API
    };
    
    updateFormData(formDataToUpdate);
    console.log('📝 Form data updated with user data:', formDataToUpdate);
    
    return true;
  };

  // Validate partial user and handle redirects
  const validatePartialUser = async (partialId: string) => {
    console.log('🔍 Validating partial user with ID:', partialId);
    
    try {
      // Call API to get partial signup data
      const response = await api.getPartialClientSignup(partialId);
      const partialData = response.data;
      console.log('📊 Partial user data from API:', partialData);
      console.log('📊 Partial data keys:', Object.keys(partialData || {}));
      
      // Use the utility function to determine the correct step
      const registrationData: Partial<RegistrationFormData> = {
        guardianFirstName: partialData.guardianFirstName,
        guardianLastName: partialData.guardianLastName,
        email: partialData.email,
        mobile: partialData.mobile,
        firstName: partialData.firstName,
        lastName: partialData.lastName,
        dob: partialData.dob,
        zipCode: partialData.zipCode,
        howDidYouHearAboutUs: partialData.howDidYouHearAboutUs,
        howDidYouHearAboutUsText: partialData.howDidYouHearAboutUsText,
      };
      
      const correctStep = determineRegistrationStep(registrationData, true);
      
      console.log('📋 Partial user validation check - determined step:', correctStep);
      
      // If user has no guardian info, they're invalid
      if (correctStep === RegistrationStep.GET_STARTED) {
        console.error('❌ Invalid partial user - missing guardian info');
        ShowToast({
          message: "Sorry, something went wrong.",
          type: "error"
        });
        
        // Clear the invalid partial token and redirect to new registration
        clearPartialToken();
        setRegistrationData(prev => ({
          ...prev,
          state: 'new',
          currentStep: RegistrationStep.GET_STARTED
        }));
        return false;
      }

      // If they have all required fields, they should be a complete user
      if (correctStep >= RegistrationStep.PERSONAL_QUESTIONS) {
        console.log('⚠️ Partial user has all required fields - should be complete user');
        return false;
      }

      console.log('✅ Partial user validation passed - has required fields for step:', correctStep);
      
      // Populate form data with partial user data
      populateFormWithUserData(partialData);
      console.log('📝 Form data updated with partial user data');
      
      setRegistrationData(prev => ({
        ...prev,
        state: 'partial',
        partialId: partialId,
        currentStep: correctStep
      }));
      console.log('✅ Registration state set to PARTIAL - starting at step:', correctStep);
      return true;
      
    } catch (error) {
      console.error('❌ Error fetching partial user data:', error);
      
      // Clear the invalid partial token and redirect to new registration
      clearPartialToken();
      setRegistrationData(prev => ({
        ...prev,
        state: 'new',
        currentStep: RegistrationStep.GET_STARTED
      }));
      return false;
    }
  };

  // Initialize registration state based on cookies and URL params
  useEffect(() => {
    const initializeRegistration = async () => {
      console.log('🔍 Initializing registration...');
      setRegistrationData(prev => ({ ...prev, loading: true }));
      
      try {
        const token = getToken();
        const user = getUser();
        const partialToken = getPartialToken();

        console.log('📋 Registration data from cookies:', {
          hasToken: !!token,
          hasUser: !!user,
          userId: user?.id,
          hasPartialToken: !!partialToken,
          partialToken
        });
        
        // Debug: Check all cookies
        console.log('🔍 Debug - All cookies:', document.cookie);

        // Check if we have a complete user (token + user data)
        if (token && user && user.id) {
          console.log('✅ Complete user found, fetching fresh data...');
          try {
            // Call API to get fresh user data
            const userData = await api.getUserProfile(user.id);
            console.log('📊 Fresh user data from API:', userData);
            console.log('📊 User data keys:', Object.keys(userData || {}));
            
            // Validate the complete user and populate form data
            if (validateCompleteUser(userData)) {
              // Populate form data directly
              populateFormWithUserData(userData);
              
              // Use utility function to determine correct step
              const registrationData: Partial<RegistrationFormData> = {
                guardianFirstName: userData.guardianFirstName,
                guardianLastName: userData.guardianLastName,
                email: userData.email,
                mobile: userData.mobile,
                firstName: userData.firstName,
                lastName: userData.lastName,
                dob: userData.dob,
                zipCode: userData.zipCode,
                howDidYouHearAboutUs: userData.howDidYouHearAboutUs,
                howDidYouHearAboutUsText: userData.howDidYouHearAboutUsText,
                isInSchoolDaycare: userData.isInSchoolDaycare,
                hasIEP: userData.hasIEP,
                hasHadABAServices: userData.hasHadABAServices,
                primarySupportAreas: userData.primarySupportAreas,
                availabilitySchedule: userData.availabilitySchedule,
                openToTelehealth: userData.openToTelehealth,
                insuranceFiles: userData.insuranceFiles,
                hasAutismDiagnosis: userData.autismDiagnosis === 'YES' ? true : 
                                    userData.autismDiagnosis === 'NO' ? false : undefined,
                cdeUrl: userData.cdeUrl,
                lmnUrl: userData.lmnUrl,
                diagnosticProviderInfo: userData.diagnosticProviderInfo,
                letterProviderInfo: userData.letterProviderInfo,
              };
              
              const correctStep = determineRegistrationStep(registrationData, false);
              
              setRegistrationData(prev => ({
                ...prev,
                state: 'complete',
                userId: user.id,
                currentStep: correctStep
              }));
              console.log('✅ Registration state set to COMPLETE - starting at step:', correctStep);
            }
          } catch (error) {
            console.error('❌ Error fetching user profile:', error);
            // If API call fails, still use the user from cookies
            console.log('🔄 Falling back to cookie user data');
            
            // Validate the cookie user data as well
            if (validateCompleteUser(user)) {
              // Populate form data directly
              populateFormWithUserData(user);
              
              // Use utility function to determine correct step
              const registrationData: Partial<RegistrationFormData> = {
                guardianFirstName: user.guardianFirstName,
                guardianLastName: user.guardianLastName,
                email: user.email,
                mobile: user.mobile,
                firstName: user.firstName,
                lastName: user.lastName,
                dob: user.dob,
                zipCode: user.zipCode,
                howDidYouHearAboutUs: user.howDidYouHearAboutUs,
                howDidYouHearAboutUsText: user.howDidYouHearAboutUsText,
                isInSchoolDaycare: user.isInSchoolDaycare,
                hasIEP: user.hasIEP,
                hasHadABAServices: user.hasHadABAServices,
                primarySupportAreas: user.primarySupportAreas,
                availabilitySchedule: user.availabilitySchedule,
                openToTelehealth: user.openToTelehealth,
                insuranceFiles: user.insuranceFiles,
                hasAutismDiagnosis: user.autismDiagnosis === 'YES' ? true : 
                                    user.autismDiagnosis === 'NO' ? false : undefined,
                cdeUrl: user.cdeUrl,
                lmnUrl: user.lmnUrl,
                diagnosticProviderInfo: user.diagnosticProviderInfo,
                letterProviderInfo: user.letterProviderInfo,
              };
              
              const correctStep = determineRegistrationStep(registrationData, false);
              
              setRegistrationData(prev => ({
                ...prev,
                state: 'complete',
                userId: user.id,
                currentStep: correctStep
              }));
            }
          }
        }
        // Check if we have a partial signup token
        else if (partialToken) {
          console.log('🔄 Partial signup found with token:', partialToken);
          const isValidPartial = await validatePartialUser(partialToken);
          if (!isValidPartial) {
            console.log('🔄 Partial user validation failed, clearing form and staying on get started');
            clearFormData();
            clearCookies();
            clearPartialToken();
            setFormError(""); // Clear any error state
            setRegistrationData(prev => ({
              ...prev,
              state: 'new',
              currentStep: RegistrationStep.GET_STARTED
            }));
          }
        }
        // New registration
        else {
          console.log('🆕 Starting new registration');
          // Clear any existing form data and authentication state when starting fresh
          clearFormData();
          clearCookies();
          clearPartialToken();
          setFormError(""); // Clear any error state
          setRegistrationData(prev => ({
            ...prev,
            state: 'new',
            currentStep: defaultStep || RegistrationStep.GET_STARTED
          }));
          console.log('✅ Registration state set to NEW');
        }
      } catch (error) {
        console.error('❌ Error initializing registration:', error);
        setRegistrationData(prev => ({
          ...prev,
          error: 'Failed to initialize registration'
        }));
      } finally {
        setRegistrationData(prev => ({ ...prev, loading: false }));
        console.log('🏁 Registration initialization complete');
      }
    };
    initializeRegistration();
  }, [userId, defaultStep, populateFormWithUserData]);

  // Form progression handlers
  const handleGetStartedComplete = async () => {
    console.log('🚀 Get Started form completed, saving initial info...');
    
    try {
      // Set loading state
      setRegistrationData(prev => ({ ...prev, loading: true }));
      setFormError(""); // Clear any previous errors
      
      // Save initial info to create partial signup
      const response = await api.createPartialClientSignup({
        guardianFirstName: formData.guardianFirstName,
        guardianLastName: formData.guardianLastName,
        email: formData.email,
        mobile: formData.mobile,
        howDidYouHearAboutUs: formData.howDidYouHearAboutUs,
        howDidYouHearAboutUsText: formData.howDidYouHearAboutUsText,
      });
      
      console.log('✅ Initial info saved successfully:', response);
      console.log('📊 Full API response structure:', response);
      console.log('📊 Response data:', response.data);
      
      // Track registration step completion
      ReactGA.event("registration_step_completed", {
        step: "get_started",
        step_number: 1
      });
      
      // Set the partial token from response
      if (response.data && response.data.id) {
        console.log('✅ Found id in response:', response.data.id);
        setPartialToken(response.data.id);
        console.log('📝 Partial token set:', response.data.id);
      } else {
        console.warn('⚠️ No id found in response:', response.data);
        console.log('🔍 Available keys in response.data:', Object.keys(response.data || {}));
      }
      
      // Debug: Check if token was actually set
      const debugToken = getPartialToken();
      console.log('🔍 Debug - Partial token after setting:', debugToken);
      
      // Debug: Check all cookies
      console.log('🔍 Debug - All cookies after setting:', document.cookie);
      
      // Move to next step
      setRegistrationData(prev => ({
        ...prev,
        currentStep: RegistrationStep.BASIC_INFO,
        partialId: response.data?.partialId || null
      }));
      
      console.log('✅ Moved to Basic Info step');
      
    } catch (error: any) {
      console.error('❌ Error saving initial info:', error);
      
      // Extract error message from API response
      let errorMessage = "Failed to save initial information. Please try again.";
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      

      
      // Set form error instead of just showing toast
      setFormError(errorMessage);
    } finally {
      // Clear loading state
      setRegistrationData(prev => ({ ...prev, loading: false }));
    }
  };

  const handleBasicInfoComplete = async () => {
    console.log('🚀 Basic Info form completed, creating real user from partial...');
    
    try {
      // Set loading state
      setSaving(true);
      setFormError(""); // Clear any previous errors
      
      // Create real user from partial user
      const response = await api.signupFamily({
        // Required fields from LoginAuthDto
        email: formData.email, // Must match partial user email exactly
        password: formData.password,
        
        // Required fields from RegisterPatientAuthDto
        firstName: formData.firstName,
        lastName: formData.lastName,
        guardianFirstName: formData.guardianFirstName,
        guardianLastName: formData.guardianLastName,
        dob: formData.dob,
        zipCode: formData.zipCode,
        mobile: formData.mobile,
        howDidYouHearAboutUs: formData.howDidYouHearAboutUs,
        howDidYouHearAboutUsText: formData.howDidYouHearAboutUsText,
      });
      
      console.log('✅ Real user created successfully:', response);
      
      // Track registration step completion
      ReactGA.event("registration_step_completed", {
        step: "basic_info",
        step_number: 2
      });
      
      // Store the new JWT token and user data
      if (response.accessToken) {
        setToken(response.accessToken);
        console.log('🔐 JWT token stored for new user');
      }
      
      if (response.id) {
        // Backend now returns complete user object with type field
        setUser(response);
        console.log('👤 User data stored with type:', response.type);
      }
      
      // Clear the partial token since user is now complete
      clearPartialToken();
      console.log('🗑️ Partial token cleared - user is now complete');
      
      // Move to next step
      setRegistrationData(prev => ({
        ...prev,
        state: 'complete',
        userId: response.id,
        currentStep: RegistrationStep.PERSONAL_QUESTIONS,
        partialId: undefined // Clear partial ID from state
      }));
      
      console.log('✅ Moved to Personal Questions step as complete user');
      
    } catch (error: any) {
      console.error('❌ Error creating real user:', error);
      
      // Extract error message from API response
      let errorMessage = "Failed to create user account. Please try again.";
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      

      
      ShowToast({
        message: errorMessage,
        type: "error"
      });
    } finally {
      // Clear loading state
      setSaving(false);
    }
  };



  const handlePersonalQuestionsComplete = async () => {
    console.log('🚀 Personal Questions form completed, saving form data...');
    
    try {
      // Set loading state
      setSaving(true);
      
      // Get current user
      const user = getUser();
      if (!user?.id) {
        console.error('❌ No user found after registration - this should not happen with backend fix');
        throw new Error('User authentication failed - please try refreshing');
      }
      
      // Convert availability schedule to new format for API
      const availabilitySchedule = formData.availabilitySchedule
        ? convertAvailabilityScheduleToBackend(formData.availabilitySchedule)
        : undefined;
      
      // Save personal form questions
      await api.saveFormQuestions(user.id, {
        isInSchoolDaycare: formData.isInSchoolDaycare,
        hasIEP: formData.hasIEP,
        hasHadABAServices: formData.hasHadABAServices,
        primarySupportAreas: formData.primarySupportAreas,
        availabilitySchedule: availabilitySchedule,
        openToTelehealth: formData.openToTelehealth,
      });
      
      console.log('✅ Personal form questions saved successfully');
      
      // Track registration step completion
      ReactGA.event("registration_step_completed", {
        step: "personal_questions",
        step_number: 3
      });
      
      // Move to next step
      setRegistrationData(prev => ({
        ...prev,
        currentStep: RegistrationStep.INSURANCE
      }));
      
      console.log('✅ Moved to Insurance step');
      
    } catch (error: any) {
      console.error('❌ Error saving personal form questions:', error);
      
      // Extract error message from API response
      let errorMessage = "Failed to save personal information. Please try again.";
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      

      
      ShowToast({
        message: errorMessage,
        type: "error"
      });
    } finally {
      // Clear loading state
      setSaving(false);
    }
  };

  const handleInsuranceComplete = () => {
     console.log("🚀 Insurance step completed, moving to next step");
     
     // Track registration step completion
     ReactGA.event("registration_step_completed", {
       step: "insurance",
       step_number: 4
     });
     
     // Move to next step
     setRegistrationData(prev => ({
       ...prev,
       currentStep: RegistrationStep.DIAGNOSTIC
     }));
     
     console.log('✅ Moved to Diagnostic step');
  };

  const uploadInsurance = async (frontFile: File | null, backFile: File | null, isPrimary: boolean) => {
    try {
      setSaving(true);
      
      const user = getUser();
      if (!user?.id) {
        console.error('❌ No user found for insurance save - this should not happen with backend fix');
        throw new Error('User authentication failed - please try refreshing');
      }
      
      // If both files are null, this is a deletion request
      if (frontFile === null && backFile === null) {
        console.log(`🗑️ Deleting ${isPrimary ? 'primary' : 'secondary'} insurance from backend...`);
        
        const payload = {
          frontImg: null,
          backImg: null,
          isPrimary: isPrimary,
        };
        
        const response = await api.saveInsurance(user.id, payload);
        setSaving(false);
        
        if ([200, 201].includes(response.status)) {
          // Remove insurance from formData
          const updatedInsuranceFiles = formData.insuranceFiles?.filter((insurance) => insurance.isPrimary !== isPrimary) || [];
          updateFormData({ insuranceFiles: updatedInsuranceFiles });
          
          ShowToast({
            message: `${isPrimary ? 'Primary' : 'Secondary'} Insurance Removed!`,
            type: "success",
          });
        }
        return;
      }
      
      // Normal upload flow
      console.log(`📤 Uploading ${isPrimary ? 'primary' : 'secondary'} insurance...`);
      
      if (!frontFile || !backFile) {
        throw new Error('Both front and back images are required for insurance upload');
      }
      
      const frontSideResponse = await fileUpload(frontFile, user.id, GetSignedUrlUploadType.INSURANCE);
      const backSideResponse = await fileUpload(backFile, user.id, GetSignedUrlUploadType.INSURANCE);

      const payload = {
        frontImg: frontSideResponse.url,
        backImg: backSideResponse.url,
        isPrimary: isPrimary,
      };
      
      const response = await api.saveInsurance(user.id, payload);
      setSaving(false);
      
      if ([200, 201].includes(response.status)) {
        const insuranceData = {
          frontImg: frontSideResponse.url,
          backImg: backSideResponse.url,
          isPrimary: isPrimary,
        };
        
        console.log(`📝 ${isPrimary ? 'Primary' : 'Secondary'} insurance uploaded:`, insuranceData);
        
        // Update formData insuranceFiles array
        const existingInsurance = formData.insuranceFiles?.find((insurance) => insurance.isPrimary === isPrimary);
        let updatedInsuranceFiles;
        
        if (existingInsurance) {
          // Update existing insurance
          updatedInsuranceFiles = formData.insuranceFiles?.map((insurance) => 
            insurance.isPrimary === isPrimary ? insuranceData : insurance
          ) || [];
        } else {
          // Add new insurance
          updatedInsuranceFiles = [...(formData.insuranceFiles || []), insuranceData];
        }
        
        updateFormData({ insuranceFiles: updatedInsuranceFiles });
        
        ShowToast({
          message: `${isPrimary ? 'Primary' : 'Secondary'} Insurance Uploaded!`,
          type: "success",
        });
      }
    } catch (error) {
      setSaving(false);
      console.error('Error uploading insurance:', error);
      ShowToast({
        message: "Something went wrong while uploading the insurance",
        type: "error",
      });
    }
  };

  const handleGetInsurance = async () => {
    try {
      const user = getUser();
      if (!user?.id) return;

      const response = await api.getInsurance(user.id);
      if ([200, 201].includes(response.status) && response.data) {
        console.log('✅ Retrieved insurance data:', response.data);
        
        // Update form data with insurance information
        updateFormData({ insuranceFiles: response.data });
      }
    } catch (error) {
      console.error('❌ Error getting insurance:', error);
    }
  };


  const handleDiagnosticComplete = async (isSuccess: boolean) => {
    console.log('🚀 Diagnostic form completed, isSuccess:', isSuccess);
    console.log('🔍 Current formData.hasAutismDiagnosis:', formData.hasAutismDiagnosis);
    
    // Check if user selected "No" for autism diagnosis
    if (!isSuccess || formData.hasAutismDiagnosis === false) {
      console.log('❌ User selected "No" - moving to final results step');
      
      // Store as partial client for "No" users
      try {
        const user = getUser();
        if (user?.id) {
          // Save diagnostic info with "NO" status
          const payload: DiagnosticInfoDto = {
            autismDiagnosis: AutismDiagnosisStatus.NO,
          };
          
          await api.updateDiagnosticInfo(user.id, payload);
          console.log('✅ Saved "No" user as partial client');
        }
      } catch (error) {
        console.error('❌ Error saving "No" user as partial client:', error);
      }
      
      // Move to final results step
      setRegistrationData(prev => ({
        ...prev,
        currentStep: RegistrationStep.FINAL_RESULTS
      }));
      return;
    }
    
    // User selected "Yes" - proceed with API call
    try {
      // Set loading state
      setSaving(true);
      
      // Get current user
      const user = getUser();
      if (!user?.id) {
        console.error('❌ No user found for diagnostic save - this should not happen with backend fix');
        throw new Error('User authentication failed - please try refreshing');
      }
      
      // Convert boolean to enum value
      const autismDiagnosis = AutismDiagnosisStatus.YES;

      // Prepare the payload according to DiagnosticInfoDto
      const payload: DiagnosticInfoDto = {
        autismDiagnosis: autismDiagnosis,
      };

      // Handle file uploads and get URLs
      if (formData.diagnosticEvaluationFile) {
        try {
          const uploadResponse = await fileUpload(formData.diagnosticEvaluationFile, user.id, GetSignedUrlUploadType.CDE);
          payload.cdeUrl = uploadResponse.url;
        } catch (error) {
          console.error('Error uploading diagnostic evaluation file:', error);
          ShowToast({
            message: "Failed to upload diagnostic evaluation file. Please try again.",
            type: "error"
          });
          return;
        }
      } else if (formData.cdeUrl) {
        // Use existing URL if no new file was uploaded
        payload.cdeUrl = formData.cdeUrl;
      }

      if (formData.letterOfMedicalNecessityFile) {
        try {
          const uploadResponse = await fileUpload(formData.letterOfMedicalNecessityFile, user.id, GetSignedUrlUploadType.LMN);
          payload.lmnUrl = uploadResponse.url;
        } catch (error) {
          console.error('Error uploading letter of medical necessity file:', error);
          ShowToast({
            message: "Failed to upload letter of medical necessity file. Please try again.",
            type: "error"
          });
          return;
        }
      } else if (formData.lmnUrl) {
        // Use existing URL if no new file was uploaded
        payload.lmnUrl = formData.lmnUrl;
      }

      // Add provider information if provided
      if (formData.diagnosticProviderInfo && 
          formData.diagnosticProviderInfo.providerGroup && 
          formData.diagnosticProviderInfo.providerName && 
          formData.diagnosticProviderInfo.providerPhone) {
        payload.diagnosticProviderInfo = {
          providerGroup: formData.diagnosticProviderInfo.providerGroup,
          providerName: formData.diagnosticProviderInfo.providerName,
          providerPhone: formData.diagnosticProviderInfo.providerPhone,
        };
      }

      if (formData.letterProviderInfo && 
          formData.letterProviderInfo.providerGroup && 
          formData.letterProviderInfo.providerName && 
          formData.letterProviderInfo.providerPhone) {
        payload.letterProviderInfo = {
          providerGroup: formData.letterProviderInfo.providerGroup,
          providerName: formData.letterProviderInfo.providerName,
          providerPhone: formData.letterProviderInfo.providerPhone,
        };
      }
      
      // Call the new diagnostic info endpoint
      const response = await api.updateDiagnosticInfo(user.id, payload);
      
      if ([200, 201].includes(response.status)) {
        if (import.meta.env.VITE_ENV === "prod") {
          ReactGA.event("diagnostic-evaluation-completed-step4", {
            hasAutismDiagnosis: formData.hasAutismDiagnosis === true,
          });
        }
        
        // Track registration step completion
        ReactGA.event("registration_step_completed", {
          step: "diagnostic",
          step_number: 5,
          hasAutismDiagnosis: formData.hasAutismDiagnosis === true,
        });
        
        // Only show success toast once
        if (!diagnosticSuccessShown) {
          ShowToast({
            message: "Diagnostic information successfully uploaded!",
            type: "success",
          });
          setDiagnosticSuccessShown(true);
        }
        
        console.log('✅ Diagnostic information saved successfully - updating user data');
        
        // Update user data with the response from updateDiagnosticInfo
        if (response.data) {
          setUser(response.data);
          console.log('✅ User profile updated with response data');
        }
        
        // Move to final results step
        setRegistrationData(prev => ({
          ...prev,
          currentStep: RegistrationStep.FINAL_RESULTS
        }));
      }
      
    } catch (error: any) {
      console.error('❌ Error saving diagnostic information:', error);
      
      // Extract error message from API response
      let errorMessage = "Failed to save diagnostic information. Please try again.";
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      ShowToast({
        message: errorMessage,
        type: "error"
      });
    } finally {
      // Clear loading state
      setSaving(false);
    }
  };

  const handleBack = async () => {
    console.log('🔙 Back navigation requested from step:', registrationData.currentStep);
    
    // Determine the previous step based on current step and user state
    let previousStep: RegistrationStep;
    
    switch (registrationData.currentStep) {
      case RegistrationStep.BASIC_INFO:
        // All users can go back to get started from basic info
        previousStep = RegistrationStep.GET_STARTED;
        break;
        
      case RegistrationStep.PERSONAL_QUESTIONS:
        // For complete users, they can't go back to basic info
        if (registrationData.state === 'complete') {
          console.log('❌ Complete users cannot go back to basic info');
          return;
        }
        previousStep = RegistrationStep.BASIC_INFO;
        break;
        
      case RegistrationStep.INSURANCE:
        previousStep = RegistrationStep.PERSONAL_QUESTIONS;
        break;
        
      case RegistrationStep.DIAGNOSTIC:
        previousStep = RegistrationStep.INSURANCE;
        break;
        
      case RegistrationStep.FINAL_RESULTS:
        previousStep = RegistrationStep.DIAGNOSTIC;
        break;
        
      default:
        console.log('❌ No back navigation available for current step');
        return;
    }
    
    console.log('✅ Navigating back to step:', previousStep);
    
    setRegistrationData(prev => ({
      ...prev,
      currentStep: previousStep
    }));
  };

  const handleContinueToPortal = () => {
    console.log('🚀 User clicked Continue to Portal');
    
    // Track successful registration completion
    ReactGA.event("registration_completed", {
      action: "continue_to_portal"
    });
    
    // Clear the saved form data since registration is complete
    clearFormData();
    navigate('/family/dashboard');
  };

  const handleBackToHomepage = () => {
    console.log('🏠 User clicked Back to Homepage');
    
    // Track registration abandonment
    ReactGA.event("registration_abandoned", {
      action: "back_to_homepage",
      step: "final_results"
    });
    
    // Clear the saved form data since user is leaving the registration flow
    clearFormData();
    
    // Navigate to external homepage
    window.location.href = 'https://headstart.health';
  };

  return (
    <div
      className="h-screen bg-secondary overflow-auto"
      style={{ backgroundImage: "url('/images/authpage-overlay.png')" }}
    >
      <Header showSignIn />
      
      {/* Loading state while initializing registration */}
      {registrationData.loading && (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Loading registration...</p>
          </div>
        </div>
      )}
      
      {/* Main content - only show when not loading */}
      {!registrationData.loading && (
        <div className="py-10 px-4 md:px-0">
          
          <div className="flex justify-center items-center">
            <div className="max-w-[820px] lg:w-[63vw]">
              {registrationData.currentStep === RegistrationStep.GET_STARTED && (
                <GetStartedForm
                  onContinue={handleGetStartedComplete}
                  isFromLogin={registrationData.state === 'complete'}
                  error={formError}
                  formData={formData}
                  updateFormData={updateFormData}
                />
              )}

              {registrationData.currentStep >= RegistrationStep.BASIC_INFO && (
                <>
                    {registrationData.currentStep === RegistrationStep.BASIC_INFO && (
                      <BasicInfoForm
                        onContinue={handleBasicInfoComplete}
                        onBack={handleBack}
                        saving={saving}
                        formData={formData}
                        updateFormData={updateFormData}
                      />
                    )}
                    
                    {registrationData.currentStep === RegistrationStep.PERSONAL_QUESTIONS && (
                      <PersonalQuestionsForm
                        onContinue={handlePersonalQuestionsComplete}
                        saving={saving}
                        formData={formData}
                        updateFormData={updateFormData}
                      />
                    )}
                    
                    {registrationData.currentStep === RegistrationStep.INSURANCE && (
                      <InsuranceForm
                        onContinue={handleInsuranceComplete}
                        onBack={handleBack}
                        formData={formData}
                        onGetInsurance={handleGetInsurance}
                        uploadInsurance={uploadInsurance}
                        saving={saving}
                      />
                    )}
                    
                    {registrationData.currentStep === RegistrationStep.DIAGNOSTIC && (
                      <DiagnosticForm
                        onContinue={handleDiagnosticComplete}
                        onBack={handleBack}
                        formData={formData}
                        updateFormData={updateFormData}
                      />
                    )}
                    
                    {registrationData.currentStep === RegistrationStep.FINAL_RESULTS && (
                      <FinalResults
                        onBack={() => {
                          // Track user going back to diagnostic form
                          ReactGA.event("registration_step_navigation", {
                            action: "back_to_diagnostic",
                            from_step: "final_results",
                            to_step: "diagnostic"
                          });
                          
                          setRegistrationData(prev => ({
                            ...prev,
                            currentStep: RegistrationStep.DIAGNOSTIC
                          }));
                        }}
                        onContinueToPortal={handleContinueToPortal}
                        onBackToHomepage={handleBackToHomepage}
                      />
                    )}
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1 justify-center mt-10 text-sm">
            <span className="text-black font-medium">
              Already have an account?
            </span>
            <a
              href="/login"
              className="text-primary/80 font-bold hover:text-black"
            >
              Sign In
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

export function FamilyRegistration({ defaultStep }: { defaultStep?: number }) {
  return (
    <RegistrationProvider>
      <FamilyRegistrationContent defaultStep={defaultStep} />
    </RegistrationProvider>
  );
}


